import { createRootRouteWithContext, HeadContent, Link, Outlet, useRouter } from "@tanstack/react-router"
import type { QueryClient } from "@tanstack/react-query"
import { useSettings } from "@/hooks/useSettings"
import { JsonLd } from "@/components/json-ld"

/**
 * Organization + WebSite JSON-LD, built from whatever's actually set in
 * Settings rather than hardcoded, so it stays correct if contact info or
 * social links change through the admin panel. Renders once settings have
 * loaded; omitted before that rather than shipping a schema full of
 * placeholder/undefined values search engines would flag.
 */
function StructuredData() {
  const { data: settings } = useSettings()
  if (typeof window === "undefined" || !settings) return null

  const origin = window.location.origin
  const sameAs = Object.values(settings.social_links ?? {}).filter((v): v is string => !!v)

  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "@id": `${origin}/#organization`,
            name: settings.site_name,
            url: origin,
            ...(settings.logo_url && { logo: settings.logo_url }),
            ...(settings.contact_email && { email: settings.contact_email }),
            ...(settings.contact_phone && { telephone: settings.contact_phone }),
            ...(sameAs.length > 0 && { sameAs }),
          },
          {
            "@type": "WebSite",
            "@id": `${origin}/#website`,
            name: settings.site_name,
            url: origin,
            publisher: { "@id": `${origin}/#organization` },
          },
        ],
      }}
    />
  )
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl font-bold text-ink">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant hover:opacity-95"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  )
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error)
  const router = useRouter()

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button
            onClick={() => {
              router.invalidate()
              reset()
            }}
            className="inline-flex items-center justify-center rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant hover:opacity-95"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-full border border-border bg-background px-6 py-3 text-sm font-semibold hover:bg-secondary"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  )
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { title: "Nexovia IT" },
      { name: "description", content: "Nexovia IT — software, cloud and AI systems that help ambitious teams start their business world wide." },
    ],
  }),
  component: () => (
    <>
      <HeadContent />
      <StructuredData />
      <Outlet />
    </>
  ),
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
})
